import type { VehicleGroup } from '@cobliteam/common-types';
import { useVehicleGroup } from '@cobliteam/hooks';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Icon from '@cobliteam/ui/icons';
import type { PopperPlacementType } from '@material-ui/core/Popper';
import { useEffect, useState } from 'react';
import MultiSelectVehicleGroupsOverlay from './MultiSelectVehicleGroupsOverlay';

export interface MultiSelectVehicleGroupsProps {
  size?: 'sm' | 'md';
  selectedVehicleGroups: VehicleGroup[];
  isOpen?: boolean;
  placement?: PopperPlacementType;
  title?: string;
  emptyLabel?: string;
  isApplying?: boolean;
  isLoading?: boolean;
  hideEmptyGroups?: boolean;
  onChange: (groups: VehicleGroup[]) => void;
  onCancel?: () => void;
  onClose?: () => void;
  onClear?: () => void;
}

const MultiSelectVehicleGroups = ({ size = 'md', selectedVehicleGroups, isOpen, title, hideEmptyGroups, onChange, onClear, onCancel }: MultiSelectVehicleGroupsProps) => {
  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups = [], isFetching, isLoading } = getVehicleGroups();

  const [forceClosed, setForceClosed] = useState(false);
  const [selectionChanged, setSelectionChanged] = useState(false);
  const [selectedGroupsState, setSelectedGroupsState] = useState<VehicleGroup[]>(selectedVehicleGroups || []);

  const filteredGroups = hideEmptyGroups ? vehicleGroups?.filter((group) => Boolean(group.vehicleIds?.length)) : vehicleGroups;

  useEffect(() => {
    setForceClosed(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  useEffect(() => {
    setSelectedGroupsState(selectedVehicleGroups);
  }, [selectedVehicleGroups, vehicleGroups]);

  const verifySomethingChanges = (groupsToBeApplied: VehicleGroup[]) => {
    const hasNotChanged =
      groupsToBeApplied.length === selectedGroupsState.length && groupsToBeApplied.every((group) => selectedGroupsState.some((selectedGroup) => selectedGroup.id === group.id));
    setSelectionChanged(!hasNotChanged);
  };

  const onChangeSelect = (group: VehicleGroup) => {
    const isSelected = selectedGroupsState.find((selectedGroup) => selectedGroup.id === group.id);

    const updatedSelected = isSelected ? selectedGroupsState.filter((selectedGroup) => selectedGroup.id !== group.id) : [...selectedGroupsState, group];

    setSelectedGroupsState(updatedSelected);
    verifySomethingChanges(updatedSelected);
  };

  const clearFilters = () => {
    setSelectedGroupsState([]);
    verifySomethingChanges([]);
    if (onClear) onClear();
  };

  const cancel = () => {
    setForceClosed((prev) => !prev);
    setSelectedGroupsState(selectedVehicleGroups);
    verifySomethingChanges(selectedVehicleGroups);
    if (onCancel) onCancel();
  };

  const applyFilter = () => {
    onChange(selectedGroupsState);
    setForceClosed(true);
    setSelectionChanged(false);
  };

  return (
    <>
      <Dropdown
        onOpenChange={() => setForceClosed(false)}
        forceClose={forceClosed}
        overlay={
          <MultiSelectVehicleGroupsOverlay
            isLoading={isFetching || isLoading}
            vehicleGroups={filteredGroups}
            selectedGroups={selectedGroupsState}
            selectionChanged={selectionChanged}
            onChange={onChangeSelect}
            onClearFilter={clearFilters}
            onApplyFilter={applyFilter}
            onCancel={cancel}
          />
        }
      >
        <Button size={size} variant='text' startIcon={<Icon name='folders' className='size-4' />}>
          {title || 'Grupos'} {(selectedVehicleGroups?.length || 0) > 0 ? `(${selectedVehicleGroups?.length})` : ''}
        </Button>
      </Dropdown>
    </>
  );
};

MultiSelectVehicleGroups.displayName = 'MultiSelectVehicleGroups';

export default MultiSelectVehicleGroups;
